import s from './order-modal.module.scss'
import { Modal } from '@shared/ui/modal/modal';
import { Button } from '@shared/ui/button/button';
import { IconMessage, IconCalendarTime, IconClock } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { classes } from '@shared/api/services';
import { FetchingDiv } from '@shared/ui/fetching-div/fetching-div';
import { TZDate } from '@date-fns/tz';
import { useOrderModal } from '../../model/useOrderModal';
import { useOrder } from '../../api/useOrder';
import { useCancelOrderModal } from '@features/student/cancel-order/model/use-cancel-order-modal';
import { toast } from 'react-toastify';
import { TextWithEmoji } from '@shared/ui/text-with-emoji/text-with-emoji';
import { useProfile } from '@shared/api/useProfile';
import { useMemo } from 'react';
import { addMinutes, format } from 'date-fns';
import { ru } from 'date-fns/locale';
import classNames from 'classnames';
import { motion } from 'framer-motion';
import { getFullName } from '@utils/lib/getFullname';
import { getCDNPath } from '@utils/lib/getCDNpath';

export const OrderModal = () => {
	const { isOpen, close, modalParams } = useOrderModal()
	const { open: openCancelOrder } = useCancelOrderModal()

	const orderId = modalParams?.orderId
	const { data: order, isFetching } = useOrder(orderId)
	const { data: profile, isLoading: isProfileLoading } = useProfile()

	const appointment = order?.items[0]?.appointment
	const teacher = appointment?.teacher

	const navigate = useNavigate()

	// Дату считаем только после загрузки profile.timezone — иначе TZDate
	// возьмет часовой пояс браузера вместо часового пояса пользователя
	const timezone = profile?.timezone
	const startsAt = appointment?.starts_at
	const isDateLoading = isProfileLoading || (!!profile && !timezone)

	const date = useMemo(
		() => startsAt && timezone ? new TZDate(startsAt, timezone) : null,
		[startsAt, timezone]
	)

	const { dateFormat, timeStart, timeEnd, dayKey } = useMemo(() => {
		if (!date) return { dateFormat: '', timeStart: '', timeEnd: '', dayKey: null }

		return {
			dateFormat: format(date, 'd MMMM', { locale: ru }),
			timeStart: format(date, 'HH:mm'),
			timeEnd: format(addMinutes(date, 50), 'HH:mm'),
			dayKey: format(date, 'yyyy-MM-dd'),
		}
	}, [date])

	const todayKey = useMemo(
		() => timezone ? format(new TZDate(new Date(), timezone), 'yyyy-MM-dd') : null,
		[timezone]
	)

	// Строки yyyy-MM-dd сравниваются лексикографически корректно
	const isToday = dayKey !== null && dayKey === todayKey

	const modalStyle = {
		content: {
			width: 'calc(100% - 40px)',
			maxWidth: '420px',
			maxHeight: 'calc(100svh - 40px)',
			padding: '20px',
			overflow: 'auto',
		},
	}

	const handlePay = () => {
		classes.payForAppointment(order?.id).then(payment => {
			window.open(payment.data?.payment_url, '_self')
		}).catch((err) => {
			toast.error(`${err?.response?.data?.message || 'Возникла ошибка'}`)
			return err
		})
		close()
	}

	// Заказ без суммы — занятие уже оплачено или бесплатное
	const amount = Number(order?.discounted_amount ?? order?.amount ?? 0)
	const isPaidOrFree = Boolean(order) && amount <= 0

	return (
		<Modal isOpen={isOpen} close={close} title={'Заказ'} style={modalStyle}>
			<FetchingDiv className={s.card} isFetching={isFetching || isDateLoading}>
				{appointment && (
					<motion.div
						className={s.wrapper}
						initial={{ opacity: 0, y: 8 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.25, ease: 'easeInOut' }}
					>
						{teacher ? (
							<div className={s.profileCard}>
								<div className={s.avatar}>
									<img
										src={teacher?.avatar?.image ? getCDNPath(teacher.avatar.image) : getCDNPath('images/default_avatar.png')}
										alt=""
									/>
								</div>
								<div className={s.profileInfo}>
									<span className={s.name}>{getFullName(teacher)}</span>
									<div className={s.profileMeta}>
										<span><TextWithEmoji text={'🌟'} /> {teacher?.rating}</span>
										<span><TextWithEmoji text={'💬'} /> {teacher?.reviews?.length || '0'}</span>
									</div>
								</div>
								<Button
									label={<IconMessage size={16} />}
									size='small'
									className={s.messageBtn}
									onClick={() => {
										navigate('/messenger', { state: { id_user_in_chat: teacher?.id } })
										close()
									}}
								/>
							</div>
						) : null}

						<div className={s.tags}>
							<div className={classNames(s.tag, s.dateTag, { [s.today]: isToday })}>
								{isToday ? `Сегодня, ${dateFormat}` : dateFormat}
							</div>
							<div className={s.tag}>
								<IconCalendarTime size={14} />
								<span>{timeStart}-{timeEnd}</span>
							</div>
							<div className={s.tag}>
								<IconClock size={14} />
								<span>50 мин.</span>
							</div>
							<div className={s.tag}>
								<TextWithEmoji text={appointment?.instrument?.name} />
							</div>
						</div>

						{isPaidOrFree ? (
							<div className={s.statusText}>Занятие уже оплачено или бесплатное</div>
						) : (
							<div className={s.actions}>
								<Button
									label={'Оплатить'}
									size='small'
									className={s.actionBtn}
									onClick={handlePay}
								/>
								<Button
									theme='secondary'
									label={'Отменить заявку'}
									size='small'
									className={s.actionBtn}
									onClick={() => openCancelOrder({ orderId: order?.id })}
								/>
							</div>
						)}
					</motion.div>
				)}
			</FetchingDiv>
		</Modal>
	)
}
